"use client";

import * as React from "react";
import { styleDrivers } from "@/utils/componentStyle";
import { Button } from "./button";
import { Input } from "./input";
import { Text } from "../information/text";
import { Cross, Delete, Trash, X, ChevronDown } from "lucide-react";
import { cn } from "@/utils/utils";
import { SelectBlock } from "./select-advanced";

export type RuleOp =
  | "starts_with"
  | "ends_with"
  | "like"
  | "contains"
  | "not_contains"
  | "equal"
  | "not_equal"
  | "greater"
  | "greater_equal"
  | "less"
  | "less_equal"
  | "in_op"
  | "is_empty"
  | "is_not_empty"
  | "is_null"
  | "is_not_null";

export type RuleType = "or_cond" | "and_cond" | "cond";

export type FilterRule = {
  rule_type?: RuleType;
  col?: string;
  operator?: RuleOp;
  value?: string | number | boolean | Array<any>;
  rules?: FilterRule[] | any[];
  is_not: boolean;
  is_case_sensitive?: boolean;
};

export type FilterBuilderProps = {
  value: FilterRule;
  onChange: (next: FilterRule) => void;
  availableColumns?: string[];
  disabled?: boolean;
  className?: string;
  showJsonPreview?: boolean;
};

const DEFAULT_ROOT: FilterRule = {
  rule_type: "and_cond",
  rules: [],
  is_not: false,
  is_case_sensitive: false,
};

function cloneRule(rule: FilterRule): FilterRule {
  return JSON.parse(JSON.stringify(rule));
}

function ensureRoot(rule?: FilterRule): FilterRule {
  if (!rule) return cloneRule(DEFAULT_ROOT);
  if (!rule.rule_type || rule.rule_type === "cond") {
    const wrapped: FilterRule = cloneRule(DEFAULT_ROOT);
    wrapped.rules = [cloneRule(rule)];
    return wrapped;
  }
  return cloneRule(rule);
}

const STRING_OPERATORS: RuleOp[] = [
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  "like",
  "equal",
  "not_equal",
  "in_op",
  "is_empty",
  "is_not_empty",
  "is_null",
  "is_not_null",
];

const NUMBER_OPERATORS: RuleOp[] = [
  "equal",
  "not_equal",
  "greater",
  "greater_equal",
  "less",
  "less_equal",
  "in_op",
  "is_null",
  "is_not_null",
];

function RuleRow(props: {
  rule: FilterRule;
  availableColumns: string[];
  onChange: (next: FilterRule | null) => void;
  disabled?: boolean;
}) {
  const { rule, availableColumns, onChange, disabled } = props;

  const handleFieldChange = (col: string) => {
    const next = { ...rule, col };
    onChange(next);
  };
  const handleOperatorChange = (operator: RuleOp) => {
    const next = { ...rule, operator };
    onChange(next);
  };
  const handleValueChange = (value: string) => {
    const next = { ...rule, value };
    onChange(next);
  };

  return (
    <div className={styleDrivers.filterBuilder.ruleRow}>
      <select
        className={
          styleDrivers.input.root + " " + styleDrivers.filterBuilder.field
        }
        disabled={disabled}
        value={rule.col ?? ""}
        onChange={(e) => handleFieldChange(e.target.value)}
      >
        {availableColumns.length === 0 && (
          <option value="">Select column</option>
        )}
        {availableColumns.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <select
        className={
          styleDrivers.input.root + " " + styleDrivers.filterBuilder.operator
        }
        disabled={disabled}
        value={rule.operator ?? "equal"}
        onChange={(e) => handleOperatorChange(e.target.value as RuleOp)}
      >
        {STRING_OPERATORS.map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
      <Input
        className={styleDrivers.filterBuilder.value}
        disabled={disabled}
        value={String(rule.value ?? "")}
        onChange={(e) => handleValueChange(e.target.value)}
        placeholder="Value"
      />
      <div className={styleDrivers.filterBuilder.actions}>
        <Button
          variant="text"
          color="destructive"
          onClick={() => onChange(null)}
          disabled={disabled}
        >
          <div className="text-destructive">
            <Trash />
          </div>
        </Button>
      </div>
    </div>
  );
}

function Group(props: {
  node: FilterRule;
  availableColumns: string[];
  onChange: (next: FilterRule | null) => void;
  disabled?: boolean;
  isRoot?: boolean;
  depth?: number;
}) {
  const {
    node,
    onChange,
    availableColumns,
    disabled,
    isRoot,
    depth = 0,
  } = props;
  const rules = Array.isArray(node.rules) ? (node.rules as FilterRule[]) : [];

  const setRuleAt = (index: number, next: FilterRule | null) => {
    const copy: FilterRule = { ...node, rules: [...rules] };
    if (next === null) {
      (copy.rules as FilterRule[]).splice(index, 1);
    } else {
      (copy.rules as FilterRule[])[index] = next;
    }
    onChange(copy);
  };

  const addCondition = () => {
    const newCond: FilterRule = {
      rule_type: "cond",
      col: availableColumns[0] ?? "",
      operator: "equal",
      value: "",
      is_not: false,
      is_case_sensitive: false,
    };
    const copy: FilterRule = { ...node, rules: [...rules, newCond] };
    onChange(copy);
  };

  const addGroup = (type: Exclude<RuleType, "cond">) => {
    const newGroup: FilterRule = {
      rule_type: type,
      rules: [],
      is_not: false,
      is_case_sensitive: false,
    };
    const copy: FilterRule = { ...node, rules: [...rules, newGroup] };
    onChange(copy);
  };

  const removeSelf = () => onChange(null);

  const isOdd = depth % 2 === 1;
  return (
    <div
      className={
        styleDrivers.filterBuilder.groupContainer +
        (isOdd
          ? ` ${styleDrivers.filterBuilder.groupContainerOdd}`
          : ` ${styleDrivers.filterBuilder.groupContainerEven}`)
      }
    >
      <div className={styleDrivers.filterBuilder.groupHeader}>
        <div className="flex items-center gap-2">
          <Text className="text-xs text-muted-foreground">Group</Text>
          <div className="relative">
            <select
              disabled={disabled}
              className={cn(styleDrivers.input.root, "appearance-none pr-8 cursor-pointer")}
              value={node.rule_type ?? "and_cond"}
              onChange={(e) =>
                onChange({ ...node, rule_type: e.target.value as RuleType })
              }
            >
              <option value="and_cond">AND</option>
              <option value="or_cond">OR</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-muted-foreground" />
          </div>
        </div>
        <div className={styleDrivers.filterBuilder.groupControls}>
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              disabled={disabled}
              checked={!!node.is_not}
              onChange={(e) => onChange({ ...node, is_not: e.target.checked })}
            />
            Not
          </label>
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              disabled={disabled}
              checked={!!node.is_case_sensitive}
              onChange={(e) =>
                onChange({ ...node, is_case_sensitive: e.target.checked })
              }
            />
            Case sensitive
          </label>
          {!isRoot && (
            <Button
              variant="text"
              color="destructive"
              onClick={removeSelf}
              disabled={disabled}
            >
              <div className="text-destructive">
                <Trash />
              </div>
            </Button>
          )}
        </div>
      </div>

      {rules.length === 0 ? (
        <div className={styleDrivers.filterBuilder.emptyState}>
          No filters added
        </div>
      ) : (
        <div>
          {rules.map((child, idx) => (
            <div key={idx}>
              {child.rule_type === "cond" ? (
                <RuleRow
                  rule={child}
                  availableColumns={availableColumns}
                  onChange={(next) => setRuleAt(idx, next)}
                  disabled={disabled}
                />
              ) : (
                <div className="px-4 py-3">
                  <Group
                    node={child}
                    availableColumns={availableColumns}
                    onChange={(next) => setRuleAt(idx, next)}
                    disabled={disabled}
                    depth={depth + 1}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 py-2 px-4">
        <Button variant="outline" onClick={addCondition} disabled={disabled}>
          + Add rule
        </Button>
        <Button
          variant="outline"
          onClick={() => addGroup("and_cond")}
          disabled={disabled}
        >
          + Add AND group
        </Button>
        <Button
          variant="outline"
          onClick={() => addGroup("or_cond")}
          disabled={disabled}
        >
          + Add OR group
        </Button>
      </div>
    </div>
  );
}

export function FilterBuilder(props: FilterBuilderProps) {
  const {
    value,
    onChange,
    availableColumns = [],
    disabled,
    className,
    showJsonPreview,
  } = props;
  const root = ensureRoot(value);

  const handleRootChange = (next: FilterRule | null) => {
    onChange(next ?? cloneRule(DEFAULT_ROOT));
  };

  const clearAll = () => onChange({ ...root, rules: [] });

  return (
    <div
      className={
        styleDrivers.filterBuilder.root + (className ? ` ${className}` : "")
      }
    >
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={clearAll}
          disabled={disabled}
          leftIcon={<X className="w-4 h-4" />}
        >
          Clear Filters
        </Button>
      </div>

      <Group
        node={root}
        onChange={handleRootChange}
        availableColumns={availableColumns}
        disabled={disabled}
        isRoot
        depth={0}
      />

      {showJsonPreview && (
        <div className={styleDrivers.filterBuilder.jsonPreview}>
          <pre>{JSON.stringify(root, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

export default FilterBuilder;
